import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { logger } from '@/utils/logger'
import { CytologyFormSchema } from '@/types/formSchema'

interface ImageData {
  id: string | number  // Support both UUID (snapshots) and integer (slides)
  file_name: string
  thumbnail_url: string
  patient_id?: number
  patient_name?: string
  upload_date: string
}

interface CytologyReportData {
  patientName: string
  age: number
  gender: string
  patientCode: string
  address: string
  province: string
  department: string
  treatingDoctor: string
  clinicalDiagnosis: string
  specimenType: string
  organisms: Record<string, boolean>
  otherFindings: Record<string, boolean>
  squamousCells: Record<string, boolean>
  glandularCells: Record<string, boolean>
  conclusion: string
  recommendation: string
  cytopathologist: string
  reportDate: Date
  selectedImages?: number[]
}

interface CytologyReportEditorProps {
  imageId?: string
  patientId?: string
}

interface TemplateField {
  id: string
  label: string
  labelVi: string
  labelEn: string
  enabled: boolean
  required: boolean
  order: number
}

interface CytologyTemplateConfig {
  hospitalName: string
  departmentName: string
  headerText: string
  footerText: string
  defaultSpecimenType: string
  defaultGender: string
  organisms: TemplateField[]
  defaultOrganisms: Record<string, boolean>
  squamousCells: TemplateField[]
  defaultSquamousCells: Record<string, boolean>
  glandularCells: TemplateField[]
  defaultGlandularCells: Record<string, boolean>
  otherFindings: TemplateField[]
  defaultOtherFindings: Record<string, boolean>
  defaultConclusion: string
  defaultRecommendation: string
}

const CytologyReportEditor: React.FC<CytologyReportEditorProps> = ({ imageId, patientId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Get slideId and patientId from URL query params
  const slideIdFromUrl = searchParams.get('slideId')
  const patientIdFromUrl = searchParams.get('patientId')

  const [showImageSelector, setShowImageSelector] = useState(false)
  const [availableImages, setAvailableImages] = useState<ImageData[]>([])
  const [selectedImages, setSelectedImages] = useState<(string | number)[]>([])
  const [isLoadingImages, setIsLoadingImages] = useState(false)
  const [templateConfig, setTemplateConfig] = useState<CytologyTemplateConfig | null>(null)

  // Dynamic form schema and data
  const [formSchema, setFormSchema] = useState<CytologyFormSchema | null>(null)
  const [customFieldsData, setCustomFieldsData] = useState<Record<string, any>>({})

  const [formData, setFormData] = useState<CytologyReportData>({
    patientName: '',
    age: 0,
    gender: 'Nữ',
    patientCode: '',
    address: '',
    province: '',
    department: '',
    treatingDoctor: '',
    clinicalDiagnosis: '',
    specimenType: 'Phết nhúng dịch',
    organisms: {
      trichomonas: false,
      candida: false,
      bacterialVaginosis: false,
      actinomyces: false,
      hsv: false,
      hpv: false
    },
    otherFindings: {
      normalCells: true,
      inflammation: false,
      radiation: false,
      iud: false,
      atrophy: false,
      postHysterectomy: false,
      endometrialCells: false
    },
    squamousCells: {
      ascus: false,
      asch: false,
      lsil: false,
      lsilHpv: false,
      hsil: false,
      squamousCarcinoma: false
    },
    glandularCells: {
      agc: false,
      ais: false,
      endocervicalAdenocarcinoma: false,
      endometrialAdenocarcinoma: false,
      adenocarcinomaNos: false,
      adenocarcinomaExtrauterine: false
    },
    conclusion: 'TẾ BÀO BIỂU MÔ CỔ TỬ CUNG - ÂM ĐẠO TRONG GIỚI HẠN BÌNH THƯỜNG (NEGATIVE FOR INTRAEPITHELIAL LESION OR MALIGNANCY)',
    recommendation: 'Kết hợp kết quả xét nghiệm HPV theo dõi theo khuyến cáo.',
    cytopathologist: '',
    reportDate: new Date(),
    selectedImages: []
  })

  // Load template configuration from API (not localStorage)
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        logger.debug('🔍 CytologyReportEditor: Loading template from API...')
        const response = await fetch('/api/v1/pathology/cytology-templates/default/')

        if (response.ok) {
          const template = await response.json()
          const config = template.config

          logger.debug('✅ Loaded template config:', config)

          // Set old template config for backward compatibility
          setTemplateConfig({
            hospitalName: config.hospitalName || 'SỞ Y TẾ THÁI NGUYÊN',
            departmentName: config.departmentName || '',
            headerText: config.headerText || 'KẾT QUẢ XÉT NGHIỆM TẾ BÀO HỌC',
            footerText: config.footerText || '',
            defaultSpecimenType: config.defaultSpecimenType || 'Phết nhúng dịch',
            defaultGender: config.defaultGender || 'Nữ',
            organisms: config.organisms || [],
            defaultOrganisms: config.defaultOrganisms || {},
            squamousCells: config.squamousCells || [],
            defaultSquamousCells: config.defaultSquamousCells || {},
            glandularCells: config.glandularCells || [],
            defaultGlandularCells: config.defaultGlandularCells || {},
            otherFindings: config.otherFindings || [],
            defaultOtherFindings: config.defaultOtherFindings || {},
            defaultConclusion: config.defaultConclusion || '',
            defaultRecommendation: config.defaultRecommendation || ''
          })

          // Load form schema for dynamic fields
          if (config.formSchema) {
            logger.debug('📋 FormSchema loaded with', config.formSchema.sections?.length, 'sections')
            setFormSchema(config.formSchema)
          }

          // Apply default values from template
          setFormData(prev => ({
            ...prev,
            gender: config.defaultGender || prev.gender,
            specimenType: config.defaultSpecimenType || prev.specimenType,
            department: config.departmentName || prev.department,
            organisms: config.defaultOrganisms || prev.organisms,
            squamousCells: config.defaultSquamousCells || prev.squamousCells,
            glandularCells: config.defaultGlandularCells || prev.glandularCells,
            otherFindings: config.defaultOtherFindings || prev.otherFindings,
            conclusion: config.defaultConclusion || prev.conclusion,
            recommendation: config.defaultRecommendation || prev.recommendation
          }))
        } else {
          logger.debug('⚠️ No default template found, using defaults')
        }
      } catch (error) {
        console.error('❌ Error loading template from API:', error)
      }
    }

    fetchTemplate()
  }, [])

  // Fetch snapshots only for patient
  const fetchPatientImages = async (patId: string) => {
    try {
      setIsLoadingImages(true)

      // First, get patient's slides to find their slide IDs
      const slidesResponse = await fetch(`/api/v1/imaging/slides/?page_size=1000`)
      let patientSlideIds: (string | number)[] = []

      if (slidesResponse.ok) {
        const slidesData = await slidesResponse.json()
        const patientSlides = (slidesData.results || slidesData).filter((slide: any) =>
          slide.patient_id?.toString() === patId
        )
        patientSlideIds = patientSlides.map((slide: any) => slide.id)
      }

      // Fetch only snapshots for patient's slides
      const snapshotsResponse = await fetch(`/api/v1/pathology/snapshots/?page_size=1000`)
      let snapshots: ImageData[] = []

      if (snapshotsResponse.ok) {
        const snapshotsData = await snapshotsResponse.json()
        const allSnapshots = snapshotsData.results || snapshotsData

        // Filter snapshots that belong to patient's slides
        snapshots = allSnapshots.filter((snap: any) =>
          patientSlideIds.includes(snap.slide)
        ).map((snap: any) => ({
          id: snap.id,  // UUID string
          file_name: snap.name || `Snapshot ${new Date(snap.created_at).toLocaleString()}`,
          thumbnail_url: snap.thumbnail_url || snap.image_url,
          patient_id: parseInt(patId),
          upload_date: snap.created_at
        }))
      }

      setAvailableImages(snapshots)
      console.log('[CytologyReportEditor] Loaded snapshots:', snapshots.length)
    } catch (error) {
      console.error('Error fetching images:', error)
      toast.error('Failed to load images')
    } finally {
      setIsLoadingImages(false)
    }
  }

  // Load patient data from URL params or props
  useEffect(() => {
    const loadPatientData = async () => {
      const patId = patientIdFromUrl || patientId
      if (patId) {
        try {
          // Fetch patient info
          const response = await fetch(`/api/v1/pathology/patients/${patId}/`)
          if (response.ok) {
            const patient = await response.json()
            logger.debug('✅ Loaded patient:', patient)

            // Pre-fill form with patient data
            setFormData(prev => ({
              ...prev,
              patientName: patient.full_name || '',
              age: patient.age || 0,
              gender: patient.gender === 'F' ? 'Nữ' : patient.gender === 'M' ? 'Nam' : 'Khác',
              patientCode: patient.patient_id || '',
              address: patient.address || '',
              province: patient.province || ''
            }))

            toast.success(`Đã load thông tin bệnh nhân: ${patient.full_name}`)
          }

          // Fetch patient images
          await fetchPatientImages(patId)
        } catch (error) {
          console.error('Error loading patient data:', error)
          toast.error('Không thể tải thông tin bệnh nhân')
        }
      }
    }

    loadPatientData()
  }, [patientIdFromUrl, patientId])

  // Auto-select slide if slideId provided
  useEffect(() => {
    const slideId = slideIdFromUrl || imageId
    if (slideId) {
      const numSlideId = parseInt(slideId)
      setSelectedImages([numSlideId])
      logger.debug('✅ Auto-selected slide:', numSlideId)
      toast.success(`Đã chọn slide #${numSlideId}`)
    }
  }, [slideIdFromUrl, imageId])

  const toggleImageSelection = (imgId: string | number) => {
    setSelectedImages(prev =>
      prev.includes(imgId)
        ? prev.filter(id => id !== imgId)
        : [...prev, imgId]
    )
  }

  // Handler for custom field changes
  const handleCustomFieldChange = (fieldId: string, value: any) => {
    setCustomFieldsData(prev => ({ ...prev, [fieldId]: value }))
  }

  // Render dynamic form fields from schema
  const renderDynamicFields = () => {
    if (!formSchema || !formSchema.sections) return null

    return formSchema.sections
      .filter(section => section.enabled)
      .map(section => (
        <div key={section.id} className="mb-6">
          <h3 className="text-lg font-semibold mb-3 text-gray-800 border-b pb-2">
            {section.title || section.id}
          </h3>
          <div className="space-y-4">
            {section.fields
              .filter(field => field.enabled && field.type !== 'checkboxGroup')
              .map(field => {
                const fieldValue = customFieldsData[field.id] || ''

                if (field.type === 'text' || field.type === 'number') {
                  return (
                    <div key={field.id}>
                      <label className="block text-sm font-medium mb-1">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <input
                        type={field.type}
                        value={fieldValue}
                        onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                        placeholder={field.placeholder}
                        required={field.required}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  )
                } else if (field.type === 'textarea') {
                  return (
                    <div key={field.id}>
                      <label className="block text-sm font-medium mb-1">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <textarea
                        value={fieldValue}
                        onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                        placeholder={field.placeholder}
                        required={field.required}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  )
                } else if (field.type === 'select' && 'options' in field) {
                  return (
                    <div key={field.id}>
                      <label className="block text-sm font-medium mb-1">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <select
                        value={fieldValue}
                        onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                        required={field.required}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="">-- Chọn --</option>
                        {field.options?.map((opt, idx) => (
                          <option key={idx} value={typeof opt === 'string' ? opt : opt.value}>
                            {typeof opt === 'string' ? opt : opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )
                } else if (field.type === 'checkbox') {
                  return (
                    <div key={field.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={!!fieldValue}
                        onChange={(e) => handleCustomFieldChange(field.id, e.target.checked)}
                        className="mr-2"
                      />
                      <label className="text-sm font-medium">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                    </div>
                  )
                }
                return null
              })}
          </div>
        </div>
      ))
  }

  const handleSaveReport = async () => {
    if (!formData.patientName || !formData.cytopathologist) {
      toast.error(t('cytology.validationError'))
      return
    }

    try {
      // Transform camelCase to snake_case for backend
      const reportData = {
        patient: patientIdFromUrl || patientId, // Include patient ID
        patient_name: formData.patientName,
        age: formData.age,
        gender: formData.gender,
        patient_code: formData.patientCode,
        address: formData.address,
        province: formData.province,
        department: formData.department,
        treating_doctor: formData.treatingDoctor,
        clinical_diagnosis: formData.clinicalDiagnosis,
        specimen_type: formData.specimenType,
        organisms: formData.organisms,
        squamous_cells: formData.squamousCells,
        glandular_cells: formData.glandularCells,
        other_findings: formData.otherFindings,
        conclusion: formData.conclusion,
        recommendation: formData.recommendation,
        cytopathologist: formData.cytopathologist,
        report_date: formData.reportDate.toISOString(),
        selected_images: selectedImages,
        custom_fields: customFieldsData  // Include dynamic form data
      }

      logger.debug('💾 Saving report with custom_fields:', customFieldsData)

      // Save to backend
      const response = await fetch('/api/v1/pathology/cytology-reports/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reportData)
      })

      if (response.ok) {
        await response.json()
        toast.success(t('cytology.saveSuccess'))

        // Navigate to report view or back
        navigate(`/pathology/reports`)
      } else {
        const errorData = await response.json()
        console.error('API Error:', errorData)
        toast.error('Failed to save report')
      }
    } catch (error) {
      console.error('Error saving report:', error)
      toast.error('Error saving report')
    }
  }

  const handleExportPDF = async () => {
    try {
      const response = await fetch('/api/v1/pathology/cytology-reports/export/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          selectedImages,
          format: 'pdf'
        })
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `cytology-report-${formData.patientCode || 'draft'}.pdf`
        a.click()
        toast.success('PDF exported successfully')
      } else {
        toast.error('Failed to export PDF')
      }
    } catch (error) {
      console.error('Error exporting PDF:', error)
      toast.error('Error exporting PDF')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Action Bar */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-6 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-800"
            >
              ← {t('common.back')}
            </button>
            <h1 className="text-xl font-bold">{t('cytology.title')}</h1>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={() => setShowImageSelector(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
            >
              📸 Select Snapshots ({selectedImages.length})
            </button>
            <button
              onClick={handleExportPDF}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
            >
              📄 Export PDF
            </button>
            <button
              onClick={handleSaveReport}
              className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
            >
              💾 {t('common.save')}
            </button>
          </div>
        </div>

        {/* Selected Snapshots Preview */}
        {selectedImages.length > 0 && (
          <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
            <h3 className="font-semibold mb-3">Selected Snapshots ({selectedImages.length})</h3>
            <div className="grid grid-cols-4 gap-4">
              {availableImages
                .filter(img => selectedImages.includes(img.id))
                .map(img => (
                  <div key={img.id} className="relative group">
                    <img
                      src={img.thumbnail_url || '/api/placeholder/150/150'}
                      alt={img.file_name}
                      className="w-full h-32 object-cover rounded-md"
                    />
                    <button
                      onClick={() => toggleImageSelection(img.id)}
                      className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Cytology Form - Import from CytologyReportTemplate */}
        <div className="bg-white p-8 rounded-lg shadow-lg">
          {/* Hospital Header */}
          {templateConfig && (
            <div className="text-center mb-6 border-b pb-4">
              <h1 className="text-xl font-bold text-gray-800">{templateConfig.hospitalName}</h1>
              <h2 className="text-lg font-semibold text-gray-700">{templateConfig.departmentName}</h2>
              {templateConfig.headerText && (
                <p className="text-sm text-gray-600 mt-2">{templateConfig.headerText}</p>
              )}
            </div>
          )}

          {/* Patient Information */}
          <div className="border-b pb-6 mb-6">
            <h2 className="text-lg font-bold mb-4">{t('cytology.patientInfo')}</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('cytology.patientName')}:</label>
                <input
                  type="text"
                  value={formData.patientName}
                  onChange={(e) => setFormData({ ...formData, patientName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('cytology.age')}:</label>
                  <input
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('cytology.gender')}:</label>
                  <input
                    type="text"
                    value={formData.gender}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('cytology.patientCode')}:</label>
                  <input
                    type="text"
                    value={formData.patientCode}
                    onChange={(e) => setFormData({ ...formData, patientCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Bethesda 2014 Results - Simplified checkboxes */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Organisms */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-bold text-green-800 mb-3">{t('cytology.organismsTitle')}</h3>
              <div className="space-y-2">
                {templateConfig?.organisms ? (
                  templateConfig.organisms
                    .filter(field => field.enabled)
                    .map(field => (
                      <label key={field.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.organisms[field.id] || false}
                          onChange={(e) => setFormData({
                            ...formData,
                            organisms: { ...formData.organisms, [field.id]: e.target.checked }
                          })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{field.labelVi}</span>
                      </label>
                    ))
                ) : (
                  Object.keys(formData.organisms).map(key => (
                    <label key={key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.organisms[key]}
                        onChange={(e) => setFormData({
                          ...formData,
                          organisms: { ...formData.organisms, [key]: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{t(`cytology.${key}`)}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Squamous Cells */}
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h3 className="font-bold text-red-800 mb-3">{t('cytology.squamousCellsTitle')}</h3>
              <div className="space-y-2">
                {templateConfig?.squamousCells ? (
                  templateConfig.squamousCells
                    .filter(field => field.enabled)
                    .map(field => (
                      <label key={field.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.squamousCells[field.id] || false}
                          onChange={(e) => setFormData({
                            ...formData,
                            squamousCells: { ...formData.squamousCells, [field.id]: e.target.checked }
                          })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{field.labelVi}</span>
                      </label>
                    ))
                ) : (
                  Object.keys(formData.squamousCells).map(key => (
                    <label key={key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.squamousCells[key]}
                        onChange={(e) => setFormData({
                          ...formData,
                          squamousCells: { ...formData.squamousCells, [key]: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{t(`cytology.${key}`)}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Glandular Cells and Other Findings */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Glandular Cells */}
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-bold text-yellow-800 mb-3">{t('cytology.glandularCellsTitle')}</h3>
              <div className="space-y-2">
                {templateConfig?.glandularCells ? (
                  templateConfig.glandularCells
                    .filter(field => field.enabled)
                    .map(field => (
                      <label key={field.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.glandularCells[field.id] || false}
                          onChange={(e) => setFormData({
                            ...formData,
                            glandularCells: { ...formData.glandularCells, [field.id]: e.target.checked }
                          })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{field.labelVi}</span>
                      </label>
                    ))
                ) : (
                  Object.keys(formData.glandularCells).map(key => (
                    <label key={key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.glandularCells[key]}
                        onChange={(e) => setFormData({
                          ...formData,
                          glandularCells: { ...formData.glandularCells, [key]: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{t(`cytology.${key}`)}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Other Findings */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-bold text-blue-800 mb-3">{t('cytology.otherFindingsTitle')}</h3>
              <div className="space-y-2">
                {templateConfig?.otherFindings ? (
                  templateConfig.otherFindings
                    .filter(field => field.enabled)
                    .map(field => (
                      <label key={field.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.otherFindings[field.id] || false}
                          onChange={(e) => setFormData({
                            ...formData,
                            otherFindings: { ...formData.otherFindings, [field.id]: e.target.checked }
                          })}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{field.labelVi}</span>
                      </label>
                    ))
                ) : (
                  Object.keys(formData.otherFindings).map(key => (
                    <label key={key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.otherFindings[key]}
                        onChange={(e) => setFormData({
                          ...formData,
                          otherFindings: { ...formData.otherFindings, [key]: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{t(`cytology.${key}`)}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Conclusion */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">{t('cytology.conclusion')}:</label>
            <textarea
              value={formData.conclusion}
              onChange={(e) => setFormData({ ...formData, conclusion: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          {/* Recommendation */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">{t('cytology.recommendation')}:</label>
            <textarea
              value={formData.recommendation}
              onChange={(e) => setFormData({ ...formData, recommendation: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          {/* Cytopathologist */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('cytology.cytopathologist')}:</label>
            <input
              type="text"
              value={formData.cytopathologist}
              onChange={(e) => setFormData({ ...formData, cytopathologist: e.target.value })}
              placeholder={t('cytology.enterDoctorName')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          {/* Dynamic Custom Fields */}
          {formSchema && (
            <div className="mt-8 pt-6 border-t-2 border-gray-200">
              <h2 className="text-xl font-bold mb-4 text-gray-800">📋 Thông tin bổ sung</h2>
              {renderDynamicFields()}
            </div>
          )}
        </div>

        {/* Image Selector Modal */}
        {showImageSelector && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-5xl shadow-lg rounded-md bg-white">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">Select Snapshots for Report</h3>
                <button
                  onClick={() => setShowImageSelector(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {isLoadingImages ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Loading snapshots...</p>
                </div>
              ) : availableImages.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No snapshots available for this patient</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-4 max-h-96 overflow-y-auto">
                  {availableImages.map(img => (
                    <div
                      key={img.id}
                      onClick={() => toggleImageSelection(img.id)}
                      className={`cursor-pointer border-2 rounded-lg p-2 transition-all ${
                        selectedImages.includes(img.id)
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <img
                        src={img.thumbnail_url || '/api/placeholder/150/150'}
                        alt={img.file_name}
                        className="w-full h-32 object-cover rounded-md mb-2"
                      />
                      <p className="text-xs text-gray-600 truncate">{img.file_name}</p>
                      {selectedImages.includes(img.id) && (
                        <div className="absolute top-2 right-2 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center">
                          ✓
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end mt-4 space-x-2">
                <button
                  onClick={() => setShowImageSelector(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Done ({selectedImages.length} selected)
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CytologyReportEditor
